import React from "react";

const experiences = [
  {
    company: "Certicámara",
    period: "Octubre 2024 — Abril 2025",
    role: "Desarrolladora Fullstack / Cloud",
    bullets: [
      "Implementación de soluciones serverless en Python (AWS Lambda / Azure Functions) orientadas a procesos críticos.",
      "OCR con Azure Form Recognizer (>95% precisión) y migración gradual de módulos React → Angular.",
      "Interfaces accesibles bajo WCAG 2.1 AA, generación de reportes en Excel y control de calidad con SonarCloud.",
    ],
  },
  {
    company: "YEDA",
    period: "Abril 2025 — Presente",
    role: "Desarrolladora Fullstack",
    bullets: [
      "Desarrollo con Angular standalone y Firebase; autenticación y permisos por roles (RBAC).",
      "Mejoras de UX con Tailwind y Angular Material, optimizando flujos clave y reduciendo fricción en formularios.",
      "Automatización de generación de PDF, CI/CD con GitHub Actions e integraciones para canales móviles.",
    ],
  },
];

const Experience = () => {
  return (
    <section id="experience" aria-label="Experiencia" className="relative py-20">
      <div className="absolute inset-0 bg-gradient-to-br from-[#f6f7f4] via-white to-[#eef2ee]" />

      <div className="relative max-w-6xl mx-auto px-4">
        <div className="mb-10">
          <h2 className="font-poppins text-3xl font-semibold tracking-tight text-slate-900">
            Experiencia Profesional
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            Experiencia enfocada en desarrollo frontend, arquitectura serverless,
            automatización y calidad, con énfasis en accesibilidad y rendimiento.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-[#3f5232]/30 via-slate-200 to-transparent md:left-1/2 md:-translate-x-1/2" />

          <div className="space-y-8">
            {experiences.map((exp, idx) => {
              const isLeft = idx % 2 === 0;

              return (
                <div
                  key={idx}
                  className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 items-start`}
                >
                  {/* Punto */}
                  <div className="absolute left-4 top-6 -translate-x-1/2 md:left-1/2 md:-translate-x-1/2">
                    <div className="h-3.5 w-3.5 rounded-full bg-[#3f5232] ring-4 ring-white/70 shadow-sm" />
                  </div>

                  {/* Columna izquierda (desktop alternado) */}
                  <div className={`${isLeft ? "md:pr-10" : "md:pr-10 md:opacity-0"}`}>
                    {isLeft && (
                      <div className="rounded-2xl border border-[#3f5232]/15 bg-white/70 backdrop-blur p-6 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {exp.company}
                          </h3>
                          <span className="text-xs font-medium text-[#3f5232] bg-[#f4f6f1] border border-[#3f5232]/15 px-3 py-1 rounded-full">
                            {exp.period}
                          </span>
                        </div>

                        <div className="mt-2 text-sm text-slate-600 font-medium">
                          {exp.role}
                        </div>

                        <div className="mt-4 h-px w-full bg-gradient-to-r from-[#3f5232]/20 via-slate-200/70 to-transparent" />

                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                          {exp.bullets.map((b, i) => (
                            <li key={i} className="flex gap-3">
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3f5232]/70 flex-shrink-0" />
                              <span className="leading-relaxed">{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Columna derecha */}
                  <div className={`${!isLeft ? "md:pl-10" : "md:pl-10 md:opacity-0"}`}>
                    {!isLeft && (
                      <div className="rounded-2xl border border-[#3f5232]/15 bg-white/70 backdrop-blur p-6 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {exp.company}
                          </h3>
                          <span className="text-xs font-medium text-[#3f5232] bg-[#f4f6f1] border border-[#3f5232]/15 px-3 py-1 rounded-full">
                            {exp.period}
                          </span>
                        </div>

                        <div className="mt-2 text-sm text-slate-600 font-medium">
                          {exp.role}
                        </div>

                        <div className="mt-4 h-px w-full bg-gradient-to-r from-[#3f5232]/20 via-slate-200/70 to-transparent" />

                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                          {exp.bullets.map((b, i) => (
                            <li key={i} className="flex gap-3">
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3f5232]/70 flex-shrink-0" />
                              <span className="leading-relaxed">{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Mobile fallback: siempre visible (cuando en desktop queda “opacity-0”) */}
                  <div className="md:hidden pl-10">
                    <div className="rounded-2xl border border-[#3f5232]/15 bg-white/70 backdrop-blur p-6 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {exp.company}
                        </h3>
                        <span className="text-xs font-medium text-[#3f5232] bg-[#f4f6f1] border border-[#3f5232]/15 px-3 py-1 rounded-full">
                          {exp.period}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-slate-600 font-medium">
                        {exp.role}
                      </div>

                      <div className="mt-4 h-px w-full bg-gradient-to-r from-[#3f5232]/20 via-slate-200/70 to-transparent" />

                      <ul className="mt-4 space-y-2 text-sm text-slate-600">
                        {exp.bullets.map((b, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3f5232]/70 flex-shrink-0" />
                            <span className="leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
